import { CartAddEvent } from '@theme/events';

/**
 * <box-builder> — fixed-price box composer for 4C Club.
 * State: one entry per product card (key = categoryId-productId) with the chosen variant + quantity.
 * Adds a single line (the box product) to the cart with the selection as line item properties.
 */
class BoxBuilder extends HTMLElement {
  /** Entries are per variant: key = cardKey:variantId
   * @type {Map<string, {key:string, cardKey:string, group:string, categoryId:string, variantId:string, title:string, image:string, qty:number, order:number}>} */
  #entries = new Map();
  #order = 0;
  #messageTimer = 0;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.required = {
      products: Number(this.dataset.productsRequired) || 0,
      accessories: Number(this.dataset.accessoriesRequired) || 0,
    };
    this.allowDuplicates = this.dataset.allowDuplicates === 'true';
    this.summary = this.querySelector('[data-summary]');
    this.submitButton = this.querySelector('[data-submit]');
    this.message = this.querySelector('[data-message]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /* ---------- events ---------- */

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const toggle = target.closest('[data-summary-toggle]');
    if (toggle) {
      const open = this.summary.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const remove = target.closest('[data-remove]');
    if (remove) {
      this.#change(remove.getAttribute('data-remove'), -1);
      return;
    }

    if (target.closest('[data-submit]')) {
      this.#submit();
      return;
    }

    const card = target.closest('[data-item]');
    if (!card) return;
    if (target.closest('[data-add]') || target.closest('[data-plus]')) this.#add(card);
    else if (target.closest('[data-minus]')) this.#change(this.#entryKey(card), -1);
  };

  #onChange = (event) => {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    if (!radio.matches('[data-variant-radio]')) return;
    const card = radio.closest('[data-item]');
    card.dataset.variantId = radio.value;
    card.dataset.variantTitle = radio.dataset.title || '';
    if (radio.dataset.image) card.dataset.image = radio.dataset.image;
    const img = card.querySelector('.box-item__media img');
    if (img && radio.dataset.imageLarge) {
      img.removeAttribute('srcset');
      img.src = radio.dataset.imageLarge;
    }
    this.#render();
  };

  /* ---------- state ---------- */

  #count(group) {
    let total = 0;
    for (const e of this.#entries.values()) if (e.group === group) total += e.qty;
    return total;
  }

  #countCategory(categoryId) {
    let total = 0;
    for (const e of this.#entries.values()) if (e.categoryId === categoryId) total += e.qty;
    return total;
  }

  #entryKey(card) {
    return `${card.dataset.itemKey}:${card.dataset.variantId}`;
  }

  #cardTotal(cardKey) {
    let total = 0;
    for (const e of this.#entries.values()) if (e.cardKey === cardKey) total += e.qty;
    return total;
  }

  #itemTitle(card) {
    const variant = card.dataset.variantTitle;
    return variant ? `${card.dataset.productTitle} (${variant})` : card.dataset.productTitle;
  }

  #add(card) {
    const key = this.#entryKey(card);
    const cardKey = card.dataset.itemKey;
    const group = card.dataset.group;
    const categoryId = card.dataset.categoryId;
    const required = this.required[group] || 0;
    const existing = this.#entries.get(key);

    if (!this.allowDuplicates && this.#cardTotal(cardKey) > 0) {
      this.#flash(card, 'Ce produit est déjà dans ta box.', true);
      return;
    }

    // Category limit
    const categoryEl = card.closest('[data-category]');
    const categoryLimit = Number(categoryEl?.dataset.categoryLimit) || 0;
    if (categoryLimit && this.#countCategory(categoryId) >= categoryLimit) {
      this.#flash(card, this.#fill(this.dataset.textCategoryFull, 0, categoryLimit), true);
      return;
    }

    // Group full: single-slot groups swap, otherwise ask to remove one first
    if (this.#count(group) >= required) {
      if (required === 1 && !existing) {
        for (const [k, e] of this.#entries) if (e.group === group) this.#entries.delete(k);
      } else {
        this.#flash(card, this.#fill(this.dataset.textFullProducts, 0, required), true);
        return;
      }
    }

    if (existing) {
      existing.qty += 1;
    } else {
      this.#entries.set(key, {
        key,
        cardKey,
        group,
        categoryId,
        variantId: card.dataset.variantId,
        title: this.#itemTitle(card),
        image: card.dataset.image || '',
        qty: 1,
        order: this.#order++,
      });
    }
    this.#clearMessage();
    this.#render();
  }

  #change(key, delta) {
    const entry = this.#entries.get(key);
    if (!entry) return;
    entry.qty += delta;
    if (entry.qty <= 0) this.#entries.delete(key);
    this.#clearMessage();
    this.#render();
  }

  /** Flattened list of units in the order they were added, grouped products → accessories */
  #units(group) {
    const units = [];
    [...this.#entries.values()]
      .filter((e) => e.group === group)
      .sort((a, b) => a.order - b.order)
      .forEach((e) => {
        for (let i = 0; i < e.qty; i++) units.push(e);
      });
    return units;
  }

  /* ---------- render ---------- */

  #render() {
    const counts = { products: this.#count('products'), accessories: this.#count('accessories') };
    const totalRequired = this.required.products + this.required.accessories;
    const totalCount = Math.min(counts.products, this.required.products) + Math.min(counts.accessories, this.required.accessories);
    const complete = counts.products === this.required.products && counts.accessories === this.required.accessories;

    // Cards
    this.querySelectorAll('[data-item]').forEach((card) => {
      const qty = this.#entries.get(this.#entryKey(card))?.qty || 0;
      const cardTotal = this.#cardTotal(card.dataset.itemKey);
      const group = card.dataset.group;
      const groupFull = counts[group] >= (this.required[group] || 0);
      card.classList.toggle('is-selected', cardTotal > 0);

      const badge = card.querySelector('[data-badge]');
      if (badge) {
        badge.hidden = cardTotal === 0;
        badge.textContent = cardTotal > 1 ? `×${cardTotal}` : '✓';
      }
      card.querySelectorAll('[data-variant-radio]').forEach((radio) => {
        const vQty = this.#entries.get(`${card.dataset.itemKey}:${radio.value}`)?.qty || 0;
        const tag = radio.closest('label')?.querySelector('[data-variant-qty]');
        if (tag) {
          tag.hidden = vQty === 0;
          tag.textContent = String(vQty);
        }
      });
      const add = card.querySelector('[data-add]');
      const stepper = card.querySelector('[data-stepper]');
      if (add && stepper) {
        add.hidden = qty > 0;
        stepper.hidden = qty === 0;
        stepper.querySelector('[data-qty]').textContent = String(qty);
        const plus = stepper.querySelector('[data-plus]');
        plus.disabled = !this.allowDuplicates || groupFull;
      }
    });

    // Slots
    ['products', 'accessories'].forEach((group) => {
      const units = this.#units(group);
      this.querySelectorAll(`[data-slot][data-slot-group="${group}"]`).forEach((slot, index) => {
        const unit = units[index];
        if (!slot.dataset.placeholder) slot.dataset.placeholder = slot.querySelector('.box-slot__name').textContent;
        const thumb = slot.querySelector('.box-slot__thumb');
        const name = slot.querySelector('.box-slot__name');
        slot.querySelector('[data-remove]')?.remove();

        if (unit) {
          slot.classList.remove('is-empty');
          thumb.style.backgroundImage = unit.image ? `url("${unit.image}")` : '';
          name.textContent = unit.title;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'box-slot__remove';
          remove.setAttribute('data-remove', unit.key);
          remove.setAttribute('aria-label', `Retirer ${unit.title}`);
          remove.textContent = '×';
          slot.append(remove);
        } else {
          slot.classList.add('is-empty');
          thumb.style.backgroundImage = '';
          name.textContent = slot.dataset.placeholder;
        }
      });
    });

    // Progress
    const parts = [this.#fill(this.dataset.textProducts, counts.products, this.required.products)];
    if (this.required.accessories > 0) {
      parts.push(this.#fill(this.dataset.textAccessories, counts.accessories, this.required.accessories));
    }
    const progressText = this.querySelector('[data-progress-text]');
    if (progressText) progressText.textContent = complete ? this.dataset.textReady : parts.join(' · ');
    const bar = this.querySelector('[data-progress-bar]');
    if (bar) bar.style.width = `${totalRequired ? (totalCount / totalRequired) * 100 : 0}%`;
    const toggleCount = this.querySelector('[data-toggle-count]');
    if (toggleCount) toggleCount.textContent = `${totalCount}/${totalRequired}`;

    // Submit
    if (this.submitButton && !this.submitButton.hasAttribute('data-unavailable')) {
      this.submitButton.disabled = !complete;
      if (!this.submitButton.dataset.label) this.submitButton.dataset.label = this.submitButton.textContent.trim();
      this.submitButton.textContent = complete
        ? this.submitButton.dataset.label
        : this.#fill(this.dataset.textRemaining, totalRequired - totalCount, totalRequired);
    }
  }

  #fill(template = '', count = 0, total = 0) {
    return template.replace(/\[count\]/g, String(count)).replace(/\[total\]/g, String(total));
  }

  #flash(card, text, isError = false) {
    card.classList.remove('is-shake');
    void card.offsetWidth;
    card.classList.add('is-shake');
    this.#showMessage(text, isError);
    if (window.matchMedia('(max-width: 989px)').matches) {
      this.summary.classList.add('is-open');
      this.querySelector('[data-summary-toggle]')?.setAttribute('aria-expanded', 'true');
    }
  }

  #showMessage(text, isError = false) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.classList.toggle('is-error', isError);
    clearTimeout(this.#messageTimer);
    this.#messageTimer = window.setTimeout(() => this.#clearMessage(), 4000);
  }

  #clearMessage() {
    if (!this.message) return;
    this.message.textContent = '';
    this.message.classList.remove('is-error');
  }

  /* ---------- cart ---------- */

  #properties() {
    const properties = {};
    const productLabel = this.dataset.productLabel || 'Produit';
    const accessoryLabel = this.dataset.accessoryLabel || 'Accessoire';

    this.#units('products').forEach((unit, i) => {
      properties[`${productLabel} ${i + 1}`] = unit.title;
    });
    const accessories = this.#units('accessories');
    accessories.forEach((unit, i) => {
      properties[accessories.length > 1 ? `${accessoryLabel} ${i + 1}` : accessoryLabel] = unit.title;
    });
    if (this.dataset.includedLabel && this.querySelector('.box-slot.is-included')) {
      properties[this.dataset.includedLabel] = this.dataset.includedValue || '✓';
    }
    // Hidden (underscore) property: variant IDs for fulfilment / future automations
    properties._box_contents = [...this.#entries.values()].map((e) => `${e.variantId}x${e.qty}`).join(',');
    return properties;
  }

  async #submit() {
    if (!this.submitButton || this.submitButton.disabled) return;
    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const body = {
      items: [{ id: Number(this.dataset.variantId), quantity: 1, properties: this.#properties() }],
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const addUrl = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showMessage(data.description || data.message || 'Une erreur est survenue.', true);
        this.submitButton.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(this.dataset.variantId), {
          source: 'box-builder',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      // Theme set to "cart page" (no drawer): go to the cart
      if (!document.querySelector('cart-drawer-component')) {
        window.location.href = this.dataset.cartUrl || '/cart';
        return;
      }

      this.#entries.clear();
      this.#render();
      this.#showMessage(this.dataset.textAdded);
    } catch (error) {
      console.error(error);
      this.#showMessage('Une erreur est survenue. Réessaie.', true);
      this.submitButton.disabled = false;
    } finally {
      this.submitButton.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('box-builder')) customElements.define('box-builder', BoxBuilder);
